<template>
  <Head :title="t('page.title', {institutionName: institution.name})"></Head>
  <s-page-block class="flex flex-col gap-5">
    <s-page-header :heading="t('header.heading')" :description="t('header.description')">
      <template #actions>
        <s-btn
          icon="plus"
          :href="
            route('institutions.lti-registrations.instructions', {
              institution: institution.id,
            })
          "
        >
          {{ t('links.instructions') }}
        </s-btn>
      </template>
    </s-page-header>

    <section v-if="ltiRegistrations.length" class="stack vertical gap-3">
      <s-link
        v-for="ltiRegistration in ltiRegistrations"
        :href="
          route('institutions.lti-registrations.lti-deployments.index', {
            institution: institution.id,
            ltiRegistration: ltiRegistration.id,
          })
        "
        class="group card-soft border border-gray-200 transition hover:from-gray-100 hover:to-gray-50 px-4 md:px-5 lg:px-6 py-2.5 md:py-4 stack horizontal align-between valign-center gap-3 text-gray-700 hover:text-blue-500"
      >
        <div class="stack vertical gap-2 w-full">
          <div class="text-lg leading-tight">
            {{ t('page.registrations.card.clientId') }}:
            {{ ltiRegistration.clientId }}
          </div>
        </div>
        <div class="stack horizontal gap-4 w-fit text-nowrap">
          <s-icon
            name="chevron-right"
            class="text-blue-500 opacity-50 group-hover:opacity-80 group-active:opacity-100 transition"
          />
        </div>
      </s-link>
    </section>
    <s-placeholder v-else>
      <p>{{ t('page.registrations.emptyList') }}</p>
    </s-placeholder>
  </s-page-block>
</template>
<script setup lang="ts">
import {Head} from '@inertiajs/vue3';
import SPageBlock from '../../design-system/SPageBlock.vue';
import SPageHeader from '../../design-system/SPageHeader.vue';
import {useI18n} from 'vue-i18n';
import {Institution} from '../../types/entities/institution';
import SBtn from '../../design-system/SBtn.vue';
import SPlaceholder from '../../design-system/SPlaceholder.vue';
import {route} from 'ziggy-js';
import SLink from '../../components/SLink.vue';
import SIcon from '../../design-system/SIcon.vue';
import {LaravelDate} from '../../types/laravel-date';

defineProps<{
  institution: Institution;
  ltiRegistrations: {
    id: number;
    platformUrl: string;
    issuer: string;
    clientId: string;
    createdByEmail: string;
    createdAt: LaravelDate;
  }[];
}>();

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});
</script>
<i18n>
{
  "en": {
    "page.title": "LTI Registrations - {institutionName}",
    "header.heading": "LTI Registrations",
    "header.description": "Connect external learning platforms (LMSs) to your tool. Each registration allows an LMS to securely launch and interact with Stemble using the LTI standard.",
    "links.instructions": "Create LTI Registration",
    "page": {
      "registrations": {
        "emptyList": "No registrations to display",
        "card": {
          "clientId": "Client ID"
        }
      }
    }
  },
  "fr": {
    "page.title": "Inscriptions LTI - {institutionName}",
    "header.heading": "Inscriptions LTI",
    "header.description": "Connectez des plateformes d'apprentissage externes (LMS) à votre outil. Chaque inscription permet à un LMS de lancer et d'interagir avec Stemble en utilisant la norme LTI.",
    "links.instructions": "Créer une inscription LTI",
    "page": {
      "registrations": {
        "emptyList": "Aucune inscription à afficher",
        "card": {
          "clientId": "ID du client"
        }
      }
    }
  }
}
</i18n>
